import { useEffect, useState } from "react";
import type { HedefNetKumesi, HedefNetSinavTuru, HedefNetYaniti } from "@koclukpanel/contracts";
import { Buton, Iskelet, Kart, netMetni } from "@koclukpanel/ui";
import { ApiHatasi, hedefNetim } from "./api";
import { hataMetni } from "./hata-metni";

/**
 * HEDEF NETLERİM — salt okunur görünüm (F-EXM-09, Emre kararı 07.10.2026)
 *
 * ================================================================
 * KOÇ GİRER, ÖĞRENCİ VE VELİ YALNIZ GÖRÜR
 * ================================================================
 * Bu dosyada düzenleme kontrolü (input, kaydet/düzenle düğmesi) YOK ve eklenmeyecek: yazma ucu bu uygulamada
 * bulunmuyor. Toplam ve ders hedefleri sunucudan olduğu gibi gelir; ekran toplamı KENDİSİ hesaplamaz
 * (derslerle çelişen ikinci bir toplam olmasın).
 *
 * Görünüm iki parçaya ayrıldı: `HedefNetleriGorunumu` veri alan SAF sunum (veli portalı TanStack Query ile
 * çeker, öğrenci kabuğu sorgu sağlayıcısızdır — `DenemeNetlerim` notu), `HedefNetlerim` öğrenci tarafının
 * veri çeken kabuğu. Aynı çizim iki yerde ayrı ayrı yazılsaydı etiketler zamanla ayrışırdı.
 */

/** Sınav türünün insan okur adı. Kısaltmalar (TYT, AYT…) olduğu gibi kalır; yalnız kod gibi duranlar çevrilir. */
const SINAV_TURU_ADI: Record<HedefNetSinavTuru, string> = {
  TYT: "TYT",
  AYT: "AYT",
  YDT: "YDT",
  LGS: "LGS",
  BRANCH: "Branş",
  KPSS: "KPSS",
  DGS: "DGS",
  OTHER: "Diğer",
};

export function sinavTuruAdi(tur: HedefNetSinavTuru): string {
  return SINAV_TURU_ADI[tur];
}

/**
 * Yetki/eski sunucu yanıtı mı (403/404)?
 *
 * Bu durumlarda kullanıcının yapabileceği bir şey yok ve sayfanın geri kalanı etkilenmemeli: kart sessizce
 * gizlenir. Ağ ya da sunucu arızasında ise kart KAYBOLMAZ — "hedef yok" sonucu çıkarılmasın diye durum yazılır
 * (veli portalındaki `Alinamadi` notuyla aynı ilke).
 */
export function hedefYokYaniMi(hata: unknown): boolean {
  return hata instanceof ApiHatasi && (hata.durum === 403 || hata.durum === 404);
}

function doluKumeler(yanit: HedefNetYaniti): HedefNetKumesi[] {
  return yanit.kumeler.filter((k) => k.dersler.length > 0);
}

export interface HedefNetleriGorunumuProps {
  yanit: HedefNetYaniti;
  /** Kart başlığı: öğrenci "Hedef netlerim", veli "Hedef netler" der. */
  baslik?: string;
  /** Hiç hedef yokken yazılacak cümle (veli ve öğrenci farklı hitap eder). */
  bosMetni?: string;
  /** Hiç hedef yokken kartı hiç çizme (hedefi olmayan öğrencide boş bir kart gürültü olur). */
  sessiz?: boolean;
}

/** Saf sunum: kümeleri sınav türüne göre gruplar, ders ve toplam hedefi yazar. */
export function HedefNetleriGorunumu({
  yanit,
  baslik = "Hedef netlerim",
  bosMetni = "Koçun henüz hedef net girmedi.",
  sessiz = false,
}: HedefNetleriGorunumuProps) {
  const kumeler = doluKumeler(yanit);
  if (kumeler.length === 0) {
    if (sessiz) return null;
    return (
      <Kart baslik={baslik}>
        <p className="text-notr-600 text-sm" data-testid="hedef-netlerim-bos">
          {bosMetni}
        </p>
      </Kart>
    );
  }
  return (
    <Kart baslik={baslik}>
      <div className="flex flex-col gap-4" data-testid="hedef-netlerim">
        {kumeler.map((k) => (
          <div key={k.examKind} className="flex flex-col gap-1.5">
            <h3 className="text-notr-900 text-sm font-semibold">{sinavTuruAdi(k.examKind)}</h3>
            <dl className="flex flex-col">
              {k.dersler.map((d) => (
                <div
                  key={d.lessonId}
                  className="border-notr-200 flex items-baseline justify-between gap-3 border-b py-1.5 text-sm"
                >
                  <dt className="text-notr-800 min-w-0">{d.ders}</dt>
                  <dd className="text-notr-900 shrink-0 font-medium tabular-nums">
                    {netMetni(d.hedefNet)}
                  </dd>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                <dt className="text-notr-900 font-semibold">Toplam hedef</dt>
                <dd className="text-notr-900 shrink-0 font-semibold tabular-nums">
                  {netMetni(k.toplam)}
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    </Kart>
  );
}

/**
 * Öğrenci tarafı: veriyi çeker, durumları yönetir.
 *
 * react-query KULLANILMIYOR (öğrenci kabuğu sağlayıcısız); `DenemeNetlerim` ile aynı useEffect kalıbı.
 * `sessiz`: hedef boşken ya da uç hata verirken hiçbir şey çizme — hedefi olmayan öğrencinin Denemeler
 * sekmesinde yeni bir "bir şey ters" izlenimi oluşmasın.
 */
export function HedefNetlerim({
  ogrenciId,
  sessiz = false,
}: {
  ogrenciId: string;
  sessiz?: boolean;
}) {
  const [yanit, setYanit] = useState<HedefNetYaniti | null>(null);
  const [gizli, setGizli] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [deneme, setDeneme] = useState(0);

  useEffect(() => {
    let gecerli = true;
    setHata(null);
    setGizli(false);
    void hedefNetim(ogrenciId).then(
      (v) => {
        if (gecerli) setYanit(v);
      },
      (e: unknown) => {
        if (!gecerli) return;
        if (hedefYokYaniMi(e)) setGizli(true);
        else setHata(hataMetni(e, "Hedef netler alınamadı."));
      },
    );
    return () => {
      gecerli = false;
    };
  }, [ogrenciId, deneme]);

  if (gizli) return null;
  if (hata) {
    if (sessiz) return null;
    return (
      <div
        role="status"
        className="rounded-alan bg-uyari-500/15 text-uyari-600 flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
      >
        <span>{hata}</span>
        <Buton gorunum="ikincil" onClick={() => setDeneme((n) => n + 1)}>
          Tekrar dene
        </Buton>
      </div>
    );
  }
  if (yanit === null) return sessiz ? null : <Iskelet satir={2} />;
  return <HedefNetleriGorunumu yanit={yanit} sessiz={sessiz} />;
}
